import React, { useState, useEffect } from "react";
import {
  Typography,
  Box,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import { InsureAssistPerformanceStyles } from "./InsureAssistPerformanceStyles";
import CustomTable, { IColumn } from "../../common/CustomTable/CustomTable";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import {
  getAllIAMetrics,
  getIAMetricsByFilter,
} from "./InsureAssistPerformanceActions";
import IAPerformanceMetrics from "./IAPerformanceMetrics";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import IAPerformanceFilters from "./IAPerformanceFilters";
import ReactMarkdown from "react-markdown";
import { IIAPerformanceFilters } from "../../models/admin/InsureAssistPerformance/InsureAssistPerformance";

const InsureAssistPerformance: React.FC = () => {
  const dispatch = useAppDispatch();
  const isMobile = useMediaQuery("(max-width:767px)");
  const { highLatency, highTokenUsage } = useAppSelector(
    (state: RootState) => state.insureAssistPeformanceMetrics
  );
  const { calledReducerType, isLoading } = useAppSelector(
    (store: RootState) => store.common
  );

  const [filters, setFilters] = useState<IIAPerformanceFilters>({
    fromDate: "",
    toDate: "",
  });

  const columns: IColumn[] = [
    {
      label: "Input",
      accessorKey: "inputMessage",
      Cell: ({ inputMessage }: any) => (
        <Box
          sx={{
            display: "flex",
            width: "15vw",
            height: "100%",
          }}
        >
          <Typography textAlign="start">{inputMessage}</Typography>
        </Box>
      ),
    },
    {
      label: "Output",
      accessorKey: "outputMessage",
      Cell: ({ outputMessage }: any) => (
        <Accordion sx={{ boxShadow: "none", backgroundColor: "transparent" }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography component="span">
              {outputMessage?.substring(0, 140)}
              {"...."}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {" "}
            <ReactMarkdown>{outputMessage}</ReactMarkdown>
          </AccordionDetails>
        </Accordion>
      ),
    },
    {
      label: "Tokens Used",
      accessorKey: "tokensUsed",
      Cell: ({ tokensUsed }: any) => (
        <Box
          sx={{
            display: "flex",
            width: "10vw",
            height: "100%",
          }}
        >
          <Typography textAlign="start">{tokensUsed}</Typography>
        </Box>
      ),
    },
  ];
  const latencyColumns: IColumn[] = [
    {
      label: "Input",
      accessorKey: "inputMessage",
      Cell: ({ inputMessage }: any) => (
        <Box
          sx={{
            display: "flex",
            width: "15vw",
            height: "100%",
          }}
        >
          <Typography textAlign="start">{inputMessage}</Typography>
        </Box>
      ),
    },
    {
      label: "Output",
      accessorKey: "outputMessage",
      Cell: ({ outputMessage }: any) => (
        <Accordion sx={{ boxShadow: "none", backgroundColor: "transparent" }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography component="span">
              {outputMessage?.substring(0, 140)}
              {"...."}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ReactMarkdown>{outputMessage}</ReactMarkdown>
          </AccordionDetails>
        </Accordion>
      ),
    },
    {
      label: "Latency (in milli-seconds)",
      accessorKey: "latency",
      Cell: ({ latency }: any) => (
        <Box
          sx={{
            display: "flex",
            width: "15vw",
            height: "100%",
          }}
        >
          <Typography textAlign="start">{latency}</Typography>
        </Box>
      ),
    },
  ];

  useEffect(() => {
    dispatch(getAllIAMetrics());
  }, []);

  const handleDateChange = (type: "from" | "to", value: string) => {
    // If value is empty, reset the corresponding filter
    if (!value) {
      setFilters((prevFilters) => {
        const newFilters: IIAPerformanceFilters = {
          ...prevFilters,
          [type === "from" ? "fromDate" : "toDate"]: "",
        };
        dispatch(getIAMetricsByFilter(newFilters));
        return newFilters;
      });
      return;
    }
    const date = new Date(value);
    const formattedDate = date.toISOString();

    setFilters((prevFilters) => {
      const newFilters: IIAPerformanceFilters = {
        ...prevFilters,
        [type === "from" ? "fromDate" : "toDate"]: formattedDate,
      };
      dispatch(getIAMetricsByFilter(newFilters));
      return newFilters;
    });
  };
  return (
    <Box 
      sx={{ paddingTop: "2rem" }}
      id="insure_assist_performance_container"
      data-test-id="insure_assist_performance_container"
      aria-label="Insure Assist Performance"
    >
      <IAPerformanceFilters
        filters={filters}
        handleDateChange={handleDateChange}
      />
      <IAPerformanceMetrics
        tokenUsageData={highTokenUsage}
        latencyUsageData={highLatency}
      />
      <Box
        sx={{
          ...InsureAssistPerformanceStyles.tableContainer,
          maxWidth: isMobile ? "calc(100vw - 48px)" : "none",
        }}
        id="token_usage_table_container"
        data-test-id="token_usage_table_container"
        aria-label="High Token Usage Records"
      >
        <Typography 
          sx={{ fontWeight: "600" }}
          id="token_usage_table_title"
          data-test-id="token_usage_table_title"
          aria-label="High Token Usage Message Records"
        >
          High Token Usage Message Records
        </Typography>
        <CustomTable
          data={highTokenUsage}
          columns={columns}
          stickyHeader
          displaySkeleton={
            isLoading && calledReducerType === "all/getAllIAMetrics"
          }
        />
      </Box>
      <Box
        sx={{
          ...InsureAssistPerformanceStyles.tableContainer,
          maxWidth: isMobile ? "calc(100vw - 48px)" : "none",
        }}
        id="latency_usage_table_container"
        data-test-id="latency_usage_table_container"
        aria-label="High Latency Usage Records"
      >
        <Typography 
          sx={{ fontWeight: "600" }}
          id="latency_usage_table_title"
          data-test-id="latency_usage_table_title"
          aria-label="High Latency Usage Message Records"
        >
          High Latency Usage Message Records
        </Typography>
        <CustomTable
          data={highLatency}
          columns={latencyColumns}
          stickyHeader
          displaySkeleton={
            isLoading && calledReducerType === "all/getAllIAMetrics"
          }
        />
      </Box>
    </Box>
  );
};
export default InsureAssistPerformance;
